import { lazy, Suspense, useEffect, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { adminRouteOpen, showsFeed } from './lib/access';
import { api } from './lib/api';
import { loadRuntimeConfig, teamToolsEnabled } from './lib/config';
import { currentClaims, isSignedIn, refreshSession, restore, signIn, signOut } from './lib/auth';
import { useAsync } from './lib/useAsync';
import { needsCanonical, parseRoute, useRoute } from './lib/routes';
import { Auction } from './screens/Auction';
import { Feed } from './screens/Feed';
import { ImportHarness } from './screens/ImportHarness';
import { Cards } from './screens/Cards';
import { Gallery } from './screens/Gallery';
import { LiveShow } from './screens/LiveShow';
import { NewListing } from './screens/NewListing';
import { Onboarding, useSellerSetup } from './screens/Onboarding';
import { SellerAuctions } from './screens/SellerAuctions';
import { SignInHost } from './screens/SignInSheet';
import { Studio } from './screens/Studio';
import { Box, Pressable, Stack, Text } from './ui/primitives';
import { ErrorNote, Loading } from './ui/state';

/**
 * The staff admin console, in its own chunk: nobody but staff should download it, and
 * the feed's bundle should not grow by a console it never shows.
 */
const AdminArea = lazy(() => import('./screens/admin/AdminArea').then((module) => ({ default: module.AdminArea })));

/** The seller dashboard's shell, for the same two reasons (GO-606). */
const SellerDashArea = lazy(() =>
  import('./screens/seller/SellerDashArea').then((module) => ({ default: module.SellerDashArea })),
);

/**
 * The app shell.
 *
 * **Views come from the URL** (`lib/routes.ts`), not from state. They used to be state,
 * which meant a reload landed everybody back on the feed and an auction — the one thing
 * here genuinely worth linking to — could not be linked to at all. The router is eighty
 * lines and no dependency; the note in that file says what would justify a library.
 */

export function App() {
  const { route, navigate } = useRoute();
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState<string>();
  const [feedNonce, setFeedNonce] = useState(0);

  useEffect(() => {
    void (async () => {
      // Before anything else: this deployment's own configuration. Sign-in cannot be
      // offered without it, and it is one request to the same origin.
      try {
        await loadRuntimeConfig();
      } catch (caught) {
        setAuthError(
          `Could not load configuration from the API (${caught instanceof Error ? caught.message : String(caught)}).`,
        );
        setReady(true);
        return;
      }

      const url = new URL(window.location.href);
      const landing = parseLanding(url.pathname);

      if (landing === 'sellerReturn') {
        // Back from Stripe. `/complete` and `/resume` are different facts — finished
        // versus the link expired — but both land the seller in the same place, and the
        // checklist re-reads the truth rather than trusting the path.
        await restore();
        navigate({ name: 'sell' }, { replace: true });
      } else {
        await restore();
      }
      setReady(true);
    })();
  }, [navigate]);

  // An unknown `/admin/…` or `/seller/dashboard/…` path renders that console's default
  // page; rewrite the address bar to match, so a copied link or the history carries the
  // page shown rather than the stale path. `needsCanonical` is generic — it compares the
  // path to the route's own — so it needed nothing for the second console.
  useEffect(() => {
    if (ready && adminRouteOpen(route, teamToolsEnabled()) && needsCanonical(window.location.pathname, route))
      navigate(route, { replace: true });
  }, [ready, route, navigate]);

  if (!ready)
    return (
      <Shell>
        <Loading what="your session" />
      </Shell>
    );

  const claims = currentClaims();

  /**
   * **The admin console is its own frame** — top bar, side nav, main column — so it renders
   * outside the harness shell, as a show does, and with the sign-in sheet beside it, or
   * its own Sign in button would open nothing. A team tool: where team tools are off its
   * route is closed and falls through to the feed below, like the import harness's.
   */
  if (route.name === 'admin' && adminRouteOpen(route, teamToolsEnabled())) {
    return (
      <>
        <Suspense fallback={<Loading what="the admin console" />}>
          <AdminArea
            page={route.page}
            onNavigate={(page) => navigate({ name: 'admin', page })}
            onHome={() => navigate({ name: 'feed' })}
          />
        </Suspense>
        <SignInHost />
      </>
    );
  }

  /** The seller dashboard's shell: the same frame, the same gate, its own chunk (GO-606). */
  if (route.name === 'sellerDash' && adminRouteOpen(route, teamToolsEnabled())) {
    return (
      <>
        <Suspense fallback={<Loading what="the seller dashboard" />}>
          <SellerDashArea
            page={route.page}
            onNavigate={(page) => navigate({ name: 'sellerDash', page })}
            onHome={() => navigate({ name: 'feed' })}
          />
        </Suspense>
        <SignInHost />
      </>
    );
  }

  /**
   * **A live show is not a page in the app; it is the app, briefly.**
   *
   * Rendered outside the shell entirely — no header, no gutter, no readable column. The
   * artboard has video edge to edge and a single ✕ to leave, and every one of those is
   * defeated by a navbar above a letterboxed player. This is also the one route where
   * mobile web has to look like the phone app rather than like a website, because it is
   * the surface people will actually be sent a link to.
   */
  if (route.name === 'show') {
    return (
      <>
        <LiveShow showId={route.showId} onBack={() => navigate({ name: 'feed' })} />
        <SignInHost />
      </>
    );
  }

  return (
    <Shell
      header={
        <Stack direction="row" gap="sm" align="center">
          <Pressable variant={route.name === 'feed' ? 'secondary' : 'quiet'} onPress={() => navigate({ name: 'feed' })}>
            Feed
          </Pressable>
          <Pressable variant={route.name === 'sell' ? 'secondary' : 'quiet'} onPress={() => navigate({ name: 'sell' })}>
            Sell
          </Pressable>
          <Pressable
            variant={route.name === 'studio' || route.name === 'studioShow' ? 'secondary' : 'quiet'}
            onPress={() => navigate({ name: 'studio' })}
          >
            Studio
          </Pressable>
          {/* Signed-in only: a card screen is meaningless to a guest, and the gate it
              serves does not apply to one. */}
          {isSignedIn() ? (
            <Pressable
              variant={route.name === 'cards' ? 'secondary' : 'quiet'}
              onPress={() => navigate({ name: 'cards' })}
            >
              Payment
            </Pressable>
          ) : null}
          <Pressable
            variant={route.name === 'design' ? 'secondary' : 'quiet'}
            onPress={() => navigate({ name: 'design' })}
          >
            Design
          </Pressable>
          {isSignedIn() ? (
            <Pressable variant="quiet" onPress={() => void signOut()}>
              Sign out
            </Pressable>
          ) : (
            <Pressable variant="primary" onPress={() => void signIn()}>
              Sign in
            </Pressable>
          )}
        </Stack>
      }
    >
      <SignInHost />
      {authError ? <ErrorNote error={{ message: authError }} /> : null}
      {/* The fail-closed path, as one tested predicate: a console route whose team tools
          are off falls *through* to the feed rather than drawing an empty main column. */}
      {showsFeed(route, teamToolsEnabled()) ? (
        <Feed
          key={feedNonce}
          onOpenSeller={() => navigate({ name: 'sell' })}
          onOpenAuction={(auctionId) => navigate({ name: 'auction', auctionId })}
          onOpenShow={(showId) => navigate({ name: 'show', showId })}
        />
      ) : null}
      {route.name === 'auction' ? (
        <Auction auctionId={route.auctionId} onBack={() => navigate({ name: 'feed' })} />
      ) : null}
      {route.name === 'studio' || route.name === 'studioShow' ? (
        <Studio
          {...(route.name === 'studioShow' ? { showId: route.showId } : {})}
          onOpenShow={(showId) => navigate({ name: 'show', showId })}
          onOpenStudioShow={(showId) => navigate({ name: 'studioShow', showId })}
          onBack={() => navigate({ name: 'studio' })}
        />
      ) : null}
      {route.name === 'design' ? <Gallery /> : null}
      {route.name === 'cards' ? <Cards /> : null}
      {/* A team tool: its route is closed where its link is hidden, not only the link (PR #44 review). */}
      {route.name === 'importHarness' && teamToolsEnabled() ? (
        claims ? (
          <ImportHarness />
        ) : (
          <Stack gap="md" align="start">
            <Text step="heading">Sign in to use the import harness</Text>
            <Pressable variant="primary" onPress={() => void signIn()}>
              Sign in
            </Pressable>
          </Stack>
        )
      ) : null}
      {route.name === 'sell' ? (
        <SellerArea
          claims={claims}
          onPublished={() => setFeedNonce((value) => value + 1)}
          onOpenAuction={(auctionId) => navigate({ name: 'auction', auctionId })}
          onOpenImport={() => navigate({ name: 'importHarness' })}
        />
      ) : null}
    </Shell>
  );
}

/**
 * The seller side, and the three states a signed-in user can be in.
 *
 * The middle one is the interesting one: **signed in, is a seller, but the token predates
 * that**. Claims are only as fresh as the token, so a user who onboards in another tab —
 * or who was admitted from the staff queue while this tab was open — has no `sellerId`
 * and every seller capability refuses them. It looks like a permissions bug and it is a
 * cache. The fix is a button, because the alternative is silently re-minting tokens on
 * every 403 and hiding a real class of failure.
 */
function SellerArea({
  claims,
  onPublished,
  onOpenAuction,
  onOpenImport,
}: {
  claims: Record<string, unknown> | undefined;
  onPublished: () => void;
  onOpenAuction: (auctionId: string) => void;
  onOpenImport: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const taxonomy = useAsync(() => api().query('catalog.taxonomy.get', {}), []);
  const setup = useSellerSetup(Boolean(claims));
  /**
   * **Whether to link the CSV import's harness** (D55): a team tool, so **never in
   * production**, where sellers import on the phone and the web is not a seller surface
   * (O32; PR #44 review). Fails closed: an environment that does not name itself shows no
   * link. Outside production it shows for a seller the server lets import, asked the way
   * the phone decides its Import inventory button: every import capability sits behind
   * `catalog-import`, evaluated per caller, so any refusal hides it.
   */
  const teamTools = teamToolsEnabled();
  const importing = useAsync(
    () =>
      claims && teamTools
        ? api()
            .query('catalog.import.list', {})
            .then(
              () => true,
              () => false,
            )
        : Promise.resolve(false),
    [Boolean(claims), teamTools],
  );

  if (!claims) {
    return (
      <Stack gap="md" align="start">
        <Text step="heading">Sign in to list something</Text>
        <Text tone="fg.muted">Listing needs an account. Browsing does not.</Text>
        <Pressable variant="primary" onPress={() => void signIn()}>
          Sign in
        </Pressable>
      </Stack>
    );
  }

  if (setup.loading) return <Loading what="your seller checklist" />;
  if (setup.error) return <ErrorNote error={setup.error} onRetry={setup.reload} />;
  if (!setup.data) return null;

  /**
   * **The gate is the checklist, not the token.**
   *
   * `claims.sellerId` appears the instant staff admit someone, which is the *first* step
   * of onboarding rather than the last — so keying on it sent a freshly admitted seller
   * straight to the listing screen, with the phone and payouts steps rendered nowhere and
   * reachable by no route. `canGoLive` is the server's own answer to the same question,
   * and it is false until every step is done.
   */
  const stillOnboarding = !(setup.data.kind === 'steps' && setup.data.checklist.canGoLive);
  if (stillOnboarding) {
    return (
      <Stack gap="lg">
        <Onboarding setup={setup.data} onChanged={setup.reload} onBecameSeller={() => window.location.reload()} />
        {claims.sellerId ? null : (
          <Stack gap="sm" align="start">
            <Text step="label" tone="fg.muted">
              Already admitted but still seeing this? Claims travel in the token, so a sign-in that predates your
              admission does not carry `sellerId` — every seller capability will refuse it until the token is re-issued.
            </Text>
            <Pressable
              variant="quiet"
              busy={refreshing}
              onPress={() => {
                setRefreshing(true);
                void refreshSession().finally(() => {
                  setRefreshing(false);
                  window.location.reload();
                });
              }}
            >
              Refresh session
            </Pressable>
          </Stack>
        )}
      </Stack>
    );
  }

  if (taxonomy.loading) return <Loading what="the categories" />;
  if (taxonomy.error) return <ErrorNote error={taxonomy.error} onRetry={taxonomy.reload} />;
  if (!taxonomy.data) return null;

  return (
    <Stack gap="xl">
      {importing.data ? (
        <Stack direction="row" gap="sm" align="center">
          <Pressable variant="secondary" onPress={onOpenImport}>
            Import CSV
          </Pressable>
          <Text step="label" tone="fg.muted">
            Team harness for the spreadsheet import. Sellers import on the phone.
          </Text>
        </Stack>
      ) : null}
      <NewListing taxonomy={taxonomy.data} onPublished={onPublished} />
      <SellerAuctions sellerId={String(claims.sellerId)} onOpenAuction={onOpenAuction} />
    </Stack>
  );
}

function Shell({ children, header }: { children?: React.ReactNode; header?: React.ReactNode }) {
  return (
    <Box background="bg.base" style={{ minHeight: '100vh' }}>
      <Box padding="lg" style={{ maxWidth: cssVar('size.container'), margin: '0 auto' }}>
        <Stack gap="xl">
          <Stack direction="row" justify="between" align="center">
            <Text step="heading">Going Once</Text>
            {header}
          </Stack>
          {children}
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * The path another system sends the browser to, recognized **before** the app decides
 * what to render: Stripe handing a seller back. It is not a view: it is consumed at boot
 * and replaced in history, so it never survives a reload as somewhere the app thinks it is.
 */
function parseLanding(pathname: string): 'sellerReturn' | undefined {
  return parseRoute(pathname).name === 'sellerReturn' ? 'sellerReturn' : undefined;
}
